import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import LoadingFallback from "@/components/LoadingFallback";
import { trpc, useTrpcClient } from "@/hooks/use-trpc";

const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const AuthCallbackPage = lazy(() =>
  import("./pages/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const ExplorerPage = lazy(() =>
  import("./pages/ExplorerPage").then((m) => ({ default: m.ExplorerPage })),
);
const AnalyticsPage = lazy(() =>
  import("./pages/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const ChatPage = lazy(() =>
  import("./pages/ChatPage").then((m) => ({ default: m.ChatPage })),
);

export function App() {
  const { queryClient, trpcClient } = useTrpcClient();

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <MotionConfig reducedMotion="user">
        <TooltipProvider>
          <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
          <Switch>
        <Route path="/login" component={LoginPage} />
        <Route path="/auth/callback" component={AuthCallbackPage} />

        <Route path="/dashboard">
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path="/explorer">
          <ProtectedRoute>
            <AppLayout>
              <ExplorerPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path="/analytics">
          <ProtectedRoute>
            <AppLayout>
              <AnalyticsPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path="/chat">
          <ProtectedRoute>
            <AppLayout>
              <ChatPage />
            </AppLayout>
          </ProtectedRoute>
        </Route>

        <Route path="/">
          <Redirect to="/dashboard" />
        </Route>

        {/* Fallback */}
        <Route>
          <Redirect to="/dashboard" />
        </Route>
          </Switch>
          </Suspense>
          </ErrorBoundary>
        </TooltipProvider>
        </MotionConfig>
      </QueryClientProvider>
    </trpc.Provider>
  );
}
